import { addDaysToDay, daysBetween } from './dates';

export interface ReorderEstimate {
  orderCount: number;
  lastOrderDay: string | null;
  intervals: number[];
  avgInterval: number | null;
  minInterval: number | null;
  maxInterval: number | null;
  daysSinceLast: number | null;
  expectedNextDay: string | null;
  toleranceDays: number | null;
  status: 'insufficient_data' | 'ok' | 'due_soon' | 'overdue';
  confidence: 'none' | 'low' | 'medium' | 'high';
  /** Plain-language explanation of the calculation, in Hebrew. */
  explanation: string;
}

/**
 * Transparent reorder estimate from order dates (Israel days, any order).
 * expected next = last order + average interval; "overdue" once past expected + tolerance,
 * tolerance = max(3 days, 10% of the average interval). "due_soon" within 5 days before expected.
 */
export function estimateReorder(orderDays: string[], today: string): ReorderEstimate {
  const days = [...new Set(orderDays)].sort();
  const base: ReorderEstimate = {
    orderCount: days.length,
    lastOrderDay: days.at(-1) ?? null,
    intervals: [],
    avgInterval: null,
    minInterval: null,
    maxInterval: null,
    daysSinceLast: days.length ? daysBetween(days.at(-1)!, today) : null,
    expectedNextDay: null,
    toleranceDays: null,
    status: 'insufficient_data',
    confidence: 'none',
    explanation: days.length === 0 ? 'אין הזמנות עדיין' : 'נדרשות לפחות 2 הזמנות כדי להעריך תדירות',
  };
  if (days.length < 2) return base;

  const intervals: number[] = [];
  for (let i = 1; i < days.length; i++) intervals.push(daysBetween(days[i - 1]!, days[i]!));
  const avg = Math.round(intervals.reduce((s, x) => s + x, 0) / intervals.length);
  const tolerance = Math.max(3, Math.round(avg * 0.1));
  const expectedNextDay = addDaysToDay(base.lastOrderDay!, avg);
  const untilExpected = daysBetween(today, expectedNextDay);
  const status: ReorderEstimate['status'] =
    untilExpected < -tolerance ? 'overdue' : untilExpected <= 5 ? 'due_soon' : 'ok';
  const confidence = intervals.length >= 4 ? 'high' : intervals.length >= 2 ? 'medium' : 'low';
  return {
    ...base,
    intervals,
    avgInterval: avg,
    minInterval: Math.min(...intervals),
    maxInterval: Math.max(...intervals),
    expectedNextDay,
    toleranceDays: tolerance,
    status,
    confidence,
    explanation:
      `הלקוח מזמין בממוצע כל ${avg} ימים (טווח ${Math.min(...intervals)}–${Math.max(...intervals)}, ${days.length} הזמנות). ` +
      `עברו ${base.daysSinceLast} ימים מההזמנה האחרונה.`,
  };
}

export const REORDER_CONFIDENCE_LABELS = { none: '—', low: 'נמוכה', medium: 'בינונית', high: 'גבוהה' } as const;
